import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import type { I18n } from './types'
import { initialLang, Lang, LANGUAGES, languageList, SOURCE_LANG, SOURCE_TEXTS, storeLang } from './languages'
import { getTexts, setCurrentTexts } from './getTexts'

export interface I18nContextValue {
    /** all texts of the active language: t.profiles.contact.sendLogin */
    t: I18n
    lang: Lang
    /** locale for Intl / toLocale*String, e.g. 'de-DE' */
    locale: string
    /** switches the language, remembers it in this browser and re-renders the app */
    setLang: (lang: Lang) => Promise<void>
    languages: { lang: Lang; name: string }[]
}

export const I18nContext = createContext<I18nContextValue | null>(null)

type Loaded = { lang: Lang; texts: I18n }

async function load(lang: Lang): Promise<Loaded> {
    try {
        return { lang, texts: await LANGUAGES[lang].load() }
    } catch (e) {
        console.error(`i18n: could not load "${lang}", using German`, e)
        return { lang: SOURCE_LANG, texts: SOURCE_TEXTS }
    }
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [loaded, setLoaded] = useState<Loaded | null>(() => {
        const lang = initialLang()
        if (lang !== SOURCE_LANG) return null // downloaded in the effect below
        setCurrentTexts(SOURCE_LANG, SOURCE_TEXTS)
        return { lang: SOURCE_LANG, texts: SOURCE_TEXTS }
    })

    useEffect(() => {
        if (loaded) return
        let cancelled = false
        load(initialLang()).then((l) => {
            if (cancelled) return
            setCurrentTexts(l.lang, l.texts)
            setLoaded(l)
        })
        return () => {
            cancelled = true
        }
    }, [loaded])

    const setLang = useCallback(async (lang: Lang) => {
        const l = await load(lang)
        if (l.lang !== lang) {
            // its download failed (offline, or a deploy replaced the files): keep the language the
            // user has and their stored choice, and say so in that language
            window.alert(getTexts().shared.app.languageLoadFailed)
            return
        }
        storeLang(l.lang)
        setCurrentTexts(l.lang, l.texts)
        setLoaded(l)
    }, [])

    const value = useMemo<I18nContextValue | null>(
        () =>
            loaded && {
                t: loaded.texts,
                lang: loaded.lang,
                locale: LANGUAGES[loaded.lang].locale,
                setLang,
                languages: languageList(),
            },
        [loaded, setLang]
    )

    // the first download of a non-German language takes a moment; render nothing until then
    if (!value) return null

    return (
        <I18nContext.Provider value={value}>
            {/* a new language remounts the pages, so texts read outside React state refresh too
                (App.tsx keeps sign-in and the global store outside, so they are not remounted) */}
            <React.Fragment key={value.lang}>{children}</React.Fragment>
        </I18nContext.Provider>
    )
}
